import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Avatar,
  Divider,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ListItemButton
} from '@mui/material';
import { Home, Chat, Analytics, AutoGraph, Menu as MenuIcon, Logout, Settings as SettingsIcon, School, Code } from '@mui/icons-material';
import TuneIcon from '@mui/icons-material/Tune';
import HandshakeIcon from '@mui/icons-material/Handshake';
import GroupIcon from '@mui/icons-material/Group';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EmailIcon from '@mui/icons-material/Email';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { logout, SERVER_URL } from '../../services/api';
import dayjs from 'dayjs';
import { useDateRange } from '../../context/DateContext';
import { useSelectedChatbot } from '../../context/SelectedChatbotContext';
import { useThemeMode } from '../../context/ThemeContext';

const drawerWidth = 272;

const menuItems = [
  {
    category: 'Seguimiento',
    items: [
      { text: 'Estadísticas', icon: <Analytics />, path: '/' },
      { text: 'Conversaciones', icon: <Chat />, path: '/conversations' },
      { text: 'Analíticas', icon: <AutoGraph />, path: '/analytics' }
    ]
  },
  {
    category: 'Asistente',
    items: [
      { text: 'Entrenamiento', icon: <School />, path: '/training' },
      { text: 'Notificaciones', icon: <EmailIcon />, path: '/notifications', requiresNotifications: true },
      { text: 'Parámetros', icon: <SettingsIcon />, path: '/settings' },
      { text: 'Implementación', icon: <Code />, path: '/implementation' },
      { text: 'ADMIN | Opciones', icon: <TuneIcon />, path: '/admin-options', adminOnly: true }
    ]
  },
  {
    category: 'Ayuda',
    items: [
      { text: 'Soporte', icon: <HelpOutlineIcon />, path: '/support' },
      { text: 'Contacto', icon: <EmailIcon />, path: '/contact' }
    ]
  },
  {
    category: 'Ajustes',
    items: [
      { text: 'Partners', icon: <HandshakeIcon />, path: '/partners' },
      { text: 'Usuarios', icon: <GroupIcon />, path: '/users' }
    ]
  },
  {
    category: 'Herramientas',
    items: [
      { text: 'Scraping & Prompts', icon: <AutoGraph />, path: '/scraping-prompt', requiresScraping: true }
    ]
  }
];

const formatRoleLabel = (role) => {
  switch (role) {
    case 'admin':
      return 'Admin';
    case 'partner':
      return 'Partner';
    case 'user':
      return 'Usuario';
    case 'readonly':
      return 'Solo lectura';
    default:
      return role || '';
  }
};

const Sidebar = ({ mobileOpen, handleDrawerToggle }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, clearUser } = useUser();
  const { setStartDate, setEndDate } = useDateRange();
  const { selectedChatbot, setSelectedChatbot, availableAssistants, clearChatbotData } = useSelectedChatbot();
  const { mode, toggleTheme } = useThemeMode();
  const isDark = mode === 'dark';

  useEffect(() => {
    try {
      // Si hay productos disponibles y no hay uno seleccionado, usar el primero
      if (user && user.products?.length > 0 && !selectedChatbot) {
        setSelectedChatbot(user.products[0].code);
      }
    } catch (error) {
      console.error('Error al cargar datos del usuario:', error);
    }
  }, [setSelectedChatbot, selectedChatbot, user]);

  const handleLogout = async () => {
    try {
      // Intentamos hacer logout en el backend
      await logout();
    } catch (error) {
      console.error('Error en logout del backend:', error);
      // Continuamos con el logout local aunque falle el backend
    } finally {
      // Limpiamos los datos locales de forma segura
      if (typeof clearChatbotData === 'function') {
        clearChatbotData();
      }
      clearUser();
      navigate('/login');
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (mobileOpen) {
      handleDrawerToggle();
    }
  };

  const handleChatbotChange = (event) => {
    setSelectedChatbot(event.target.value);
  };

  // Obtener el nombre del asistente seleccionado
  const selectedAssistantName = availableAssistants.find(
    a => a.assistant === selectedChatbot
  )?.name || 'Seleccione un asistente';

  const drawer = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'transparent',
        position: 'relative',
        px: 2,
        py: 2.5,
      }}
    >
      {/* User Profile Section */}
      <Box
        sx={{
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
          <Avatar
            src={user?.photo ? `${SERVER_URL}${user.photo}` : ''}
            onClick={() => handleNavigation('/account')}
            sx={{
              width: 46,
              height: 46,
              cursor: 'pointer',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 8px 20px rgba(0,0,0,0.25)'
                : '0 8px 20px rgba(15,23,42,0.10)',
              '&:hover': {
                transform: 'scale(1.05)',
                transition: 'transform 0.2s ease-in-out'
              }
            }}
          />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 800,
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                color: theme.palette.text.primary,
                wordWrap: 'break-word',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: '1.2em',
                maxHeight: '2.4em',
                mb: 0.5
              }}
            >
              {user?.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                display: 'block',
                fontSize: '0.74rem',
                lineHeight: 1.2,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                mb: 0.35,
              }}
            >
              {formatRoleLabel(user?.rol)}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.primary.main,
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                display: 'none',
                fontSize: '0.75rem',
                lineHeight: 1,
                fontWeight: 600,
              }}
            >
              {user?.organizations?.find(org => org.code === user.organization)?.name || ''}
            </Typography>
          </Box>
        </Box>

        {/* Selector de chatbot */}
        {user && (
          <FormControl
            fullWidth
            variant="outlined"
            size="small"
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                color: theme.palette.text.primary,
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                borderRadius: '16px',
                backgroundColor: theme.palette.background.paper,
                '& fieldset': {
                  borderColor: theme.palette.divider,
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.light,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                }
              },
              '& .MuiInputLabel-root': {
                color: theme.palette.text.secondary,
                backgroundColor: theme.palette.background.default,
                padding: '0 8px',
                marginLeft: '-4px',
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                fontWeight: 600,
              },
              '& .MuiInputLabel-shrink': {
                transform: 'translate(14px, -9px) scale(0.75)',
              }
            }}
          >
            <InputLabel id="select-chatbot-label">Cliente</InputLabel>
            <Select
              labelId="select-chatbot-label"
              value={selectedChatbot || ''}
              onChange={handleChatbotChange}
              label="Producto"
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: theme.palette.background.paper,
                    '& .MuiMenuItem-root': {
                      color: theme.palette.text.primary,
                      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
                    },
                    '& .MuiMenuItem-root:hover': {
                      bgcolor: theme.palette.action.hover
                    }
                  }
                }
              }}
            >
              {availableAssistants.length > 0 ? (
                availableAssistants.map((assistant) => (
                  <MenuItem
                    key={assistant.assistant}
                    value={assistant.assistant}
                    sx={{
                      color: theme.palette.text.primary,
                      pl: `${16 + (assistant.level_tree || 0) * 16}px`,
                      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover
                      },
                      '&.Mui-selected': {
                        backgroundColor: theme.palette.action.selected,
                        '&:hover': {
                          backgroundColor: theme.palette.action.selected,
                        }
                      }
                    }}
                  >
                    {assistant.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No hay asistentes disponibles
                </MenuItem>
              )}
            </Select>
          </FormControl>
        )}
      </Box>

      <Box sx={{ px: 1, pt: 1, pb: 1.5 }}>
        <Divider sx={{ mb: 1.5, opacity: 0.6 }} />
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            px: 0.5,
            lineHeight: 1.1,
            letterSpacing: '-0.03em',
            fontSize: '2rem',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
          }}
        >
          <Box component="span" sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : '#0f172a' }}>AI</Box>
          <Box component="span" sx={{ color: '#f97316' }}>4</Box>
          <Box component="span" sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : '#0f172a' }}>Assistant</Box>
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Menu Items */}
        <List
          sx={{
            width: '100%',
            p: 0,
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            pr: 0.5,
            scrollbarWidth: 'thin',
            scrollbarColor: `${theme.palette.text.disabled} transparent`,
            '&::-webkit-scrollbar': {
              width: 6,
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.22)' : 'rgba(15,23,42,0.20)',
              borderRadius: 999,
            },
          }}
        >
          {menuItems.map((category) => {
            // Filtrar los items según el rol del usuario y las opciones habilitadas
            const filteredItems = category.items.filter(item => {
              // Verificar si el item requiere notificaciones habilitadas
              if (item.requiresNotifications) {
                // Buscar el producto seleccionado
                const selectedProduct = user?.products?.find(p => p.code === selectedChatbot);

                // Verificar si las notificaciones están habilitadas (NOTIFICATIONS=1)
                let notificationsEnabled = false;
                if (selectedProduct?.options) {
                  // Buscar NOTIFICATIONS=1 en cualquier posición de la cadena de opciones
                  if (selectedProduct.options.includes('NOTIFICATIONS=1')) {
                    notificationsEnabled = true;
                  }
                }

                // Si las notificaciones no están habilitadas, no mostrar el item
                if (!notificationsEnabled) return false;

              }

              // Verificar si el item requiere scraping habilitado
              if (item.requiresScraping) {
                // Si es admin, siempre mostrar
                if (user?.rol === 'admin') return true;

                // Buscar el producto seleccionado
                const selectedProduct = user?.products?.find(p => p.code === selectedChatbot);

                // Verificar si el scraping está habilitado (Scraping=yes)
                let scrapingEnabled = false;
                if (selectedProduct?.options) {
                  if (selectedProduct.options.includes('Scraping=yes') ||
                    selectedProduct.options.includes('SCRAPING=yes') ||
                    selectedProduct.options.includes('Scraping=1') ||
                    selectedProduct.options.includes('SCRAPING=1')) {
                    scrapingEnabled = true;
                  }
                }

                // Si el scraping no está habilitado, no mostrar el item
                if (!scrapingEnabled) return false;
              }

              // Solo admins ven herramientas especiales que no son scraping
              if (item.adminOnly && user?.rol !== 'admin') return false;
              // Los admin pueden ver todo
              if (user?.rol === 'admin') return true;
              // Partners pueden ver todo excepto Partners y Scraping (a menos que esté habilitado)
              if (user?.rol === 'partner') {
                // Si es Scraping, ya se verificó antes si está habilitado
                if (item.requiresScraping) return true;
                return item.text !== 'Partners';
              }
              // Users y readonly solo pueden ver lo básico (ni Partners ni Gestión)
              if (user?.rol === 'user' || user?.rol === 'readonly') {
                // Si es Scraping, ya se verificó antes si está habilitado
                if (item.requiresScraping) return true;
                return item.text !== 'Partners' && item.text !== 'Usuarios';
              }
              // Users y readonly solo pueden ver lo básico (ni Partners ni Gestión)
              if (user?.rol === 'user' || user?.rol === 'readonly') {
                return item.text !== 'Partners' && item.text !== 'Usuarios' && item.text !== 'Scraping & Prompts';
              }
              return false; // Si no tiene rol, no ve nada
            });

            // Si no hay items después del filtrado, no mostrar la categoría
            if (filteredItems.length === 0) return null;

            return (
              <Box key={category.category} sx={{ mb: 1.75, ...(category.category === 'Ajustes' && { borderTop: `1px solid ${theme.palette.divider}`, pt: 1.5 }) }}>
                <ListItem
                  sx={{
                    backgroundColor: 'transparent',
                    borderRadius: '4px',
                    mb: 0.5,
                    py: 0.25,
                    px: 0.5,
                  }}
                >
                  <ListItemText
                    primary={category.category}
                    primaryTypographyProps={{
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                      color: theme.palette.text.secondary
                    }}
                  />
                </ListItem>

                <List component="div" disablePadding>
                  {filteredItems.map((item) => {
                    const selected = item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path);
                    return (
                      <ListItem
                        key={item.text}
                        disablePadding
                        sx={{
                          mb: 0.4,
                        }}
                      >
                        <ListItemButton
                          selected={selected}
                          onClick={() => handleNavigation(item.path)}
                          sx={{
                            borderRadius: '14px',
                            py: 1.05,
                            px: 1.5,
                            color: theme.palette.text.secondary,
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            minHeight: 48,
                            '& .MuiListItemIcon-root': {
                              color: 'inherit',
                              minWidth: 38,
                            },

                            '&.Mui-selected': {
                              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(249, 115, 22, 0.12)' : 'rgba(249, 115, 22, 0.10)',
                              color: theme.palette.primary.main,
                              boxShadow: theme.palette.mode === 'dark'
                                ? 'inset 0 0 0 1px rgba(249,115,22,0.18)'
                                : 'inset 0 0 0 1px rgba(249,115,22,0.14)',
                              '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(249, 115, 22, 0.16)' : 'rgba(249, 115, 22, 0.14)',
                              },
                            },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(15,23,42,0.04)',
                              color: theme.palette.text.primary,
                              transform: 'translateX(4px)',
                            },
                          }}
                        >
                          <ListItemIcon>
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={item.text}
                            primaryTypographyProps={{
                              fontSize: '0.96rem',
                              fontWeight: selected ? 700 : 600,
                              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            );
          })}
        </List>
      </Box>

      {/* Selector de modo claro/oscuro */}
      <Box sx={{ mt: 'auto', pt: 2, px: 0.5 }}>

        <Divider sx={{ mb: 1.5, opacity: 0.6 }} />
        <Box
          onClick={toggleTheme}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            mb: 0.75,
            py: 1.1,
            px: 1.5,
            borderRadius: '14px',
            cursor: 'pointer',
            color: theme.palette.text.secondary,
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(15,23,42,0.04)',
              color: theme.palette.text.primary,
            }
          }}
        >
          <Box sx={{
            width: 34,
            height: 34,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: isDark ? 'rgba(249,115,22,0.15)' : 'rgba(15,23,42,0.06)',
          }}>
            {isDark ? <LightModeIcon sx={{ fontSize: 18, color: '#f97316' }} /> : <DarkModeIcon sx={{ fontSize: 18, color: '#475569' }} />}
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit' }}>
            {isDark ? 'Modo Claro' : 'Modo Oscuro'}
          </Typography>
        </Box>

        <ListItemButton
          onClick={() => handleNavigation('/account')}
          sx={{
            borderRadius: '14px',
            py: 1.1,
            px: 1.5,
            mb: 0.75,
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(15,23,42,0.04)',
              color: theme.palette.text.primary,
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 38, color: 'inherit' }}>
            <ManageAccountsIcon />
          </ListItemIcon>
          <ListItemText primary="Cuenta" primaryTypographyProps={{ fontSize: '0.96rem', fontWeight: 600 }} />
        </ListItemButton>

        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: '14px',
            py: 1.1,
            px: 1.5,
            color: '#ef4444',
            '& .MuiListItemIcon-root': { color: 'inherit', minWidth: 38 },
            '&:hover': {
              backgroundColor: 'rgba(239,68,68,0.06)',
              color: '#dc2626',
            }
          }}
        >
          <ListItemIcon>
            <Logout />
          </ListItemIcon>
          <ListItemText primary="Cerrar Sesión" primaryTypographyProps={{ fontSize: '0.96rem', fontWeight: 600 }} />
        </ListItemButton>

        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 2.5,
            textAlign: 'center',
            color: theme.palette.text.secondary,
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
            fontSize: '0.72rem',
            opacity: 0.45,
            fontWeight: 700,
            letterSpacing: '0.10em'
          }}
        >
          V 1.6.2 - AI4Assistant
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Botón de menú para móviles */}
      <IconButton
        color="inherit"
        aria-label="open drawer"
        edge="start"
        onClick={handleDrawerToggle}
        sx={{
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 1200,
          display: { xs: 'block', sm: 'none' },
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.background.paper,
          boxShadow: '0px 3px 8px rgba(0, 0, 0, 0.2)',
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          }
        }}
      >
        <MenuIcon />
      </IconButton>

      {/* Drawer temporal para móviles */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            borderRight: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.92)' : 'rgba(255, 255, 255, 0.84)',
            backdropFilter: 'blur(30px) saturate(180%)',
            WebkitBackdropFilter: 'blur(30px) saturate(180%)',
            boxShadow: theme.palette.mode === 'dark' ? '0 10px 40px rgba(0,0,0,0.28)' : '0 10px 40px rgba(15,23,42,0.08)'
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Drawer permanente para pantallas grandes */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.92)' : 'rgba(255, 255, 255, 0.84)',
            backdropFilter: 'blur(30px) saturate(180%)',
            WebkitBackdropFilter: 'blur(30px) saturate(180%)',
            boxShadow: theme.palette.mode === 'dark' ? '0 10px 40px rgba(0,0,0,0.18)' : '0 10px 40px rgba(15,23,42,0.04)'
          }
        }}
        open
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Sidebar;